import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Map, FileText, BarChart2, AlertTriangle, Monitor, Users, Settings, Bell, Search, Menu, Shield, Smartphone, Database, History, LogOut, ChevronDown, Briefcase, Palette, Activity } from 'lucide-react';
import { Toaster } from 'sonner';
import { useScreenInit } from './useScreenInit';
import { GlobalSearch } from './components/GlobalSearch';
// Pages
import NationalDashboard from './pages/NationalDashboard';
import RegionalDashboard from './pages/RegionalDashboard';
import RoleHome from './pages/RoleHome';
import CommandCenter from './pages/CommandCenter';
import Reports from './pages/Reports';
import ReportDetail from './pages/ReportDetail';
import ReportCreate from './pages/ReportCreate';
import ReportAnalysis from './pages/ReportAnalysis';
import IntelligenceSummary from './pages/IntelligenceSummary';
import UkstrList from './pages/UkstrList';
import UkstrForm from './pages/UkstrForm';
import UkstrMonitoring from './pages/UkstrMonitoring';
import UkstrAnswers from './pages/UkstrAnswers';
import Login from './pages/Login';
import MobileApp from './pages/MobileApp';
import KPI from './pages/KPI';
import KPIDetail from './pages/KPIDetail';
import KPIBindaDetail from './pages/KPIBindaDetail';
import KPIUnitDetail from './pages/KPIUnitDetail';
import KPIPersonnelDetail from './pages/KPIPersonnelDetail';
import KPILeaderboard from './pages/KPILeaderboard';
import Alerts from './pages/Alerts';
import AlertDetail from './pages/AlertDetail';
import Personnel from './pages/Personnel';
import PersonnelDetail from './pages/PersonnelDetail';
import UsersPage from './pages/Users';
import UserDetail from './pages/UserDetail';
import MasterData from './pages/MasterData';
import SettingsPage from './pages/Settings';
import AuditLog from './pages/AuditLog';
import ExecutiveMode from './pages/ExecutiveMode';
import DesignSystem from './pages/DesignSystem';
import Notifications from './pages/Notifications';
import PermissionDenied from './pages/PermissionDenied';
import { isRouteAllowed } from './data/rbac';
import type { Role } from './data/rbac';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './components/Dialog';
import { Button } from './components/Button';
import { Clock } from 'lucide-react';
import { SplashScreen } from './components/SplashScreen';
function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>(localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional');
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const handleStorageChange = () => {
      setRole(localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // Simulated idle session timeout. Demo window kept short for review.
  const SESSION_IDLE_MS = 5 * 60 * 1000;
  const standalone = location.pathname === '/login' || location.pathname === '/mobile' || location.pathname === '/command-center' || location.pathname === '/executive';
  useEffect(() => {
    if (standalone) return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setSessionExpired(true), SESSION_IDLE_MS);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [standalone]);
  // Demo Role Selector is the default landing experience. Until a role has
  // been chosen on the login screen (demo or enterprise), gate every route to
  // /login so stakeholders always start at the one-click role selector.
  const authed = localStorage.getItem('bimcs_authed') === 'true';
  if (!authed && location.pathname !== '/login') {
    return <Navigate to="/login" replace data-id="element-0" />;
  }
  if (!isRouteAllowed(location.pathname, role)) {
    return <Navigate to="/unauthorized" replace data-id="element-1" />;
  }
  const handleRoleChange = (newRole: string) => {
    setRole(newRole);
    localStorage.setItem('bimcs_role', newRole);
    setShowRoleMenu(false);
    if (newRole === 'Personel Lapangan') {
      navigate('/mobile');
    } else if (newRole === 'Operator Command Center') {
      navigate('/command-center');
    } else {
      navigate('/');
    }
  };
  const isCommandCenter = location.pathname === '/command-center';
  const isExecutive = location.pathname === '/executive';
  const isLogin = location.pathname === '/login';
  const isMobile = location.pathname === '/mobile';
  if (isLogin || isMobile) {
    return <div className="min-h-screen bg-background text-foreground font-sans" data-id="element-2">
        {children}
        <Toaster theme="dark" position="top-right" data-id="element-3" />
      </div>;
  }
  if (isCommandCenter || isExecutive) {
    return <div className="min-h-screen bg-background text-foreground font-sans" data-id="element-4">
        {children}
        <Toaster theme="dark" position="top-right" data-id="element-5" />
      </div>;
  }
  // Role-based nav filtering
  const allNavItems = [{
    name: 'Beranda',
    path: '/',
    icon: LayoutDashboard
  }, {
    name: 'Komando Regional',
    path: '/regional',
    icon: Map
  }, {
    name: 'Penjabaran UK/STR',
    path: '/ukstr',
    icon: FileText
  }, {
    name: 'Monitoring Tugas',
    path: '/ukstr/monitoring',
    icon: Activity
  }, {
    name: 'Jawaban Lapangan',
    path: '/ukstr/answers',
    icon: Users
  }, {
    name: 'Laporan Intelijen',
    path: '/reports',
    icon: FileText
  }, {
    name: 'KPI & Analitik',
    path: '/kpi',
    icon: BarChart2
  }, {
    name: 'Peringatan Dini',
    path: '/alerts',
    icon: AlertTriangle
  }, {
    name: 'Notifikasi',
    path: '/notifications',
    icon: Bell
  }, {
    name: 'Pusat Komando',
    path: '/command-center',
    icon: Monitor
  }, {
    name: 'Personel',
    path: '/personnel',
    icon: Users
  }, {
    name: 'Manajemen Pengguna',
    path: '/users',
    icon: Shield
  }, {
    name: 'Data Master',
    path: '/master-data',
    icon: Database
  }, {
    name: 'Log Audit',
    path: '/audit-log',
    icon: History
  }, {
    name: 'Mode Eksekutif',
    path: '/executive',
    icon: Briefcase
  }, {
    name: 'Pengaturan',
    path: '/settings',
    icon: Settings
  }, {
    name: 'Sistem Desain',
    path: '/design-system',
    icon: Palette
  }];
  const navItems = allNavItems.filter(item => isRouteAllowed(item.path, role));
  return <div className="flex h-screen bg-background text-foreground overflow-hidden font-sans" data-id="element-6">
      <Toaster theme="dark" position="top-right" data-id="element-7" />
      {/* Sidebar */}
      <aside className="w-64 bg-sidebar border-r border-sidebar-border flex flex-col z-20 shadow-[4px_0_24px_rgba(0,0,0,0.5)]" data-id="element-8">
        <div className="h-14 flex items-center px-4 border-b border-sidebar-border bg-sidebar/80 backdrop-blur-md" data-id="element-9">
          <div className="flex items-center gap-3" data-id="element-10">
            <div className="w-8 h-8 flex items-center justify-center shrink-0" data-id="element-11">
              <img src="https://cdn.magicpatterns.com/uploads/hos8i69ZV8fMbtkJgKVvED/Logo_BIN_NEW.png" alt="Logo Badan Intelijen Negara Republik Indonesia" className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(var(--primary),0.3)]" data-id="element-12" />
            </div>
            <div className="flex flex-col" data-id="element-13">
              <span className="font-heading font-bold text-sm tracking-widest text-primary text-glow-primary" data-id="element-14">
                DENS CAKRA
              </span>
              <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-mono leading-none" data-id="element-15">
                Pusat Komando Nasional
              </span>
              <span className="text-[8px] italic tracking-wider text-primary/70 font-mono leading-none mt-0.5" data-id="element-16">
                Velox Et Exactus
              </span>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5 custom-scrollbar" data-id="element-17">
          <div className="text-[10px] font-mono font-semibold text-sidebar-foreground/40 uppercase tracking-widest mb-3 px-3" data-id="element-18">
            Operasional
          </div>
          {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-all duration-200 ${isActive ? 'bg-primary/10 border-l-2 border-primary text-primary shadow-[inset_0_0_20px_rgba(var(--primary),0.05)]' : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground border-l-2 border-transparent'}`} data-id="element-19">
                <Icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-20" />
                <span className="text-sm font-medium tracking-wide" data-id="element-21">
                  {item.name}
                </span>
              </Link>;
        })}
        </nav>

        <div className="p-3 border-t border-sidebar-border bg-sidebar/50 relative" data-id="element-22">
          {showRoleMenu && <div className="absolute bottom-full left-3 right-3 mb-2 bg-card border border-border rounded shadow-lg overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2" data-id="element-23">
              <div className="p-2 text-[10px] font-mono text-muted-foreground uppercase tracking-widest border-b border-border/50" data-id="element-24">
                Simulasi Peran
              </div>
              {['Super Admin Nasional', 'Administrator Nasional', 'Kepala BINDA', 'Wakil Kepala BINDA', 'Admin BINDA', 'Analis', 'Koordinator Wilayah', 'Operator Command Center', 'Personel Lapangan'].map(r => <button key={r} onClick={() => handleRoleChange(r)} className={`w-full text-left px-3 py-2 text-xs font-mono hover:bg-primary/10 hover:text-primary transition-colors ${role === r ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`} data-id="element-25">
                  {r}
                </button>)}
              <div className="border-t border-border/50" data-id="element-26">
                <button onClick={() => {
              localStorage.removeItem('bimcs_authed');
              navigate('/login');
            }} className="w-full text-left px-3 py-2 text-xs font-mono text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2" data-id="element-27">
                  <LogOut className="w-3 h-3" data-id="element-28" /> Keluar
                </button>
              </div>
            </div>}
          <button onClick={() => setShowRoleMenu(!showRoleMenu)} className="w-full flex items-center justify-between p-2 rounded-md bg-background/50 border border-border/50 hover:border-primary/50 transition-colors" data-id="element-29">
            <div className="flex items-center gap-3" data-id="element-30">
              <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center border border-border" data-id="element-31">
                <span className="text-xs font-mono font-bold text-primary" data-id="element-32">
                  {role.split(' ').map(w => w[0]).join('').substring(0, 2)}
                </span>
              </div>
              <div className="flex flex-col text-left" data-id="element-33">
                <span className="text-xs font-medium tracking-wide truncate w-32" data-id="element-34">
                  {role}
                </span>
                <span className="text-[10px] text-muted-foreground font-mono" data-id="element-35">
                  ID: {role === 'Super Admin Nasional' ? 'SA-001-HQ' : 'OP-772'}
                </span>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showRoleMenu ? 'rotate-180' : ''}`} data-id="element-36" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] bg-repeat" data-id="element-37">
        {/* Topbar */}
        <header className="h-14 glass-panel-heavy border-b-0 flex items-center justify-between px-6 shrink-0 z-10" data-id="element-38">
          <div className="flex items-center gap-4" data-id="element-39">
            <button className="lg:hidden text-muted-foreground hover:text-foreground" data-id="element-40">
              <Menu className="w-5 h-5" data-id="element-41" />
            </button>
            <button onClick={() => setSearchOpen(true)} className="relative hidden md:flex items-center w-80 bg-background/50 border border-border/50 hover:border-primary/50 rounded-sm py-1.5 px-3 text-left transition-all group" data-id="element-42">
              <Search className="w-4 h-4 text-muted-foreground group-hover:text-primary mr-2 shrink-0 transition-colors" data-id="element-43" />
              <span className="text-xs font-mono text-muted-foreground/50 flex-1" data-id="element-44">
                Cari basis data intelijen...
              </span>
              <div className="flex items-center gap-1 shrink-0" data-id="element-45">
                <span className="text-[10px] font-mono text-muted-foreground bg-white/5 px-1.5 py-0.5 rounded border border-border/30" data-id="element-46">
                  ⌘K
                </span>
              </div>
            </button>
          </div>

          <div className="flex items-center gap-5" data-id="element-47">
            <div className="flex items-center gap-2 px-3 py-1 rounded bg-background/50 border border-border/50" data-id="element-48">
              <span className="relative flex h-2 w-2" data-id="element-49">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--status-active)] opacity-75" data-id="element-50"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--status-active)]" data-id="element-51"></span>
              </span>
              <span className="text-xs font-mono text-[var(--status-active)] uppercase tracking-wider text-glow-active" data-id="element-52">
                Sistem Normal
              </span>
            </div>
            <div className="h-4 w-px bg-border" data-id="element-53"></div>
            <button onClick={() => navigate('/notifications')} aria-label="Lihat pusat notifikasi" className="relative text-muted-foreground hover:text-primary transition-colors" data-id="element-54">
              <Bell className="w-4 h-4" data-id="element-55" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-destructive shadow-[0_0_5px_var(--destructive)]" data-id="element-56"></span>
            </button>
            <button onClick={() => navigate('/settings')} aria-label="Buka pengaturan" className="text-muted-foreground hover:text-primary transition-colors" data-id="element-57">
              <Settings className="w-4 h-4" data-id="element-58" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 relative custom-scrollbar" data-id="element-59">
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background pointer-events-none -z-10" data-id="element-60"></div>
          {children}
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} data-id="element-61" />

      <Dialog open={sessionExpired} onOpenChange={setSessionExpired} data-id="element-62">
        <DialogContent showCloseButton={false} className="glass-panel-heavy border-destructive/30" data-id="element-63">
          <DialogHeader data-id="element-64">
            <div className="w-12 h-12 mx-auto bg-destructive/10 rounded-full flex items-center justify-center border border-destructive/30 mb-2" data-id="element-65">
              <Clock className="w-6 h-6 text-destructive" data-id="element-66" />
            </div>
            <DialogTitle className="text-center font-heading tracking-widest uppercase text-destructive" data-id="element-67">
              Sesi Berakhir
            </DialogTitle>
            <DialogDescription className="text-center font-mono text-xs" data-id="element-68">
              Sesi Anda telah berakhir karena tidak ada aktivitas. Untuk alasan
              keamanan, silakan otentikasi ulang untuk melanjutkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter data-id="element-69">
            <Button onClick={() => {
            setSessionExpired(false);
            localStorage.removeItem('bimcs_authed');
            navigate('/login');
          }} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase" data-id="element-70">
              Masuk Kembali
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}
export function App() {
  useScreenInit();
  // Brief boot splash on initial load. Skipped inside the Screens canvas so
  // each screen preview renders immediately.
  const isScreens = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('mp_screen');
  const [booting, setBooting] = useState(!isScreens);
  useEffect(() => {
    if (!booting) return;
    const t = setTimeout(() => setBooting(false), 2200);
    return () => clearTimeout(t);
  }, [booting]);
  if (booting) {
    return <SplashScreen data-id="element-71" />;
  }
  return <BrowserRouter data-id="element-72">
      <Layout data-id="element-73">
        <Routes data-id="element-74">
          <Route path="/" element={<RoleHome data-id="element-76" />} data-id="element-75" />
          <Route path="/regional/:kode?" element={<RegionalDashboard data-id="element-78" />} data-id="element-77" />
          <Route path="/command-center" element={<CommandCenter data-id="element-80" />} data-id="element-79" />
          <Route path="/executive" element={<ExecutiveMode data-id="element-82" />} data-id="element-81" />
          <Route path="/reports" element={<Reports data-id="element-84" />} data-id="element-83" />
          <Route path="/ukstr" element={<UkstrList data-id="element-86" />} data-id="element-85" />
          <Route path="/ukstr/new" element={<UkstrForm data-id="element-88" />} data-id="element-87" />
          <Route path="/ukstr/monitoring" element={<UkstrMonitoring data-id="element-90" />} data-id="element-89" />
          <Route path="/ukstr/answers" element={<UkstrAnswers data-id="element-92" />} data-id="element-91" />
          <Route path="/ukstr/:id" element={<UkstrForm data-id="element-94" />} data-id="element-93" />
          <Route path="/reports/new" element={<ReportCreate data-id="element-96" />} data-id="element-95" />
          <Route path="/reports/intelligence-summary" element={<IntelligenceSummary data-id="element-98" />} data-id="element-97" />
          <Route path="/reports/:id" element={<ReportDetail data-id="element-100" />} data-id="element-99" />
          <Route path="/reports/:id/analysis" element={<ReportAnalysis data-id="element-102" />} data-id="element-101" />
          <Route path="/login" element={<Login data-id="element-104" />} data-id="element-103" />
          <Route path="/mobile" element={<MobileApp data-id="element-106" />} data-id="element-105" />
          <Route path="/kpi" element={<KPI data-id="element-108" />} data-id="element-107" />
          <Route path="/kpi/leaderboard" element={<KPILeaderboard data-id="element-110" />} data-id="element-109" />
          <Route path="/kpi/binda/:kode" element={<KPIBindaDetail data-id="element-112" />} data-id="element-111" />
          <Route path="/kpi/unit/:kode/:unitId" element={<KPIUnitDetail data-id="element-114" />} data-id="element-113" />
          <Route path="/kpi/personnel/:personId" element={<KPIPersonnelDetail data-id="element-116" />} data-id="element-115" />
          <Route path="/kpi/:kode" element={<KPIDetail data-id="element-118" />} data-id="element-117" />
          <Route path="/alerts" element={<Alerts data-id="element-120" />} data-id="element-119" />
          <Route path="/alerts/:id" element={<AlertDetail data-id="element-122" />} data-id="element-121" />
          <Route path="/personnel" element={<Personnel data-id="element-124" />} data-id="element-123" />
          <Route path="/personnel/:id" element={<PersonnelDetail data-id="element-126" />} data-id="element-125" />
          <Route path="/users" element={<UsersPage data-id="element-128" />} data-id="element-127" />
          <Route path="/users/:id" element={<UserDetail data-id="element-130" />} data-id="element-129" />
          <Route path="/master-data" element={<MasterData data-id="element-132" />} data-id="element-131" />
          <Route path="/settings" element={<SettingsPage data-id="element-134" />} data-id="element-133" />
          <Route path="/audit-log" element={<AuditLog data-id="element-136" />} data-id="element-135" />
          <Route path="/notifications" element={<Notifications data-id="element-138" />} data-id="element-137" />
          <Route path="/design-system" element={<DesignSystem data-id="element-140" />} data-id="element-139" />
          <Route path="/unauthorized" element={<PermissionDenied data-id="element-142" />} data-id="element-141" />
          <Route path="*" element={<Navigate to="/" replace data-id="element-144" />} data-id="element-143" />
        </Routes>
      </Layout>
    </BrowserRouter>;
}